/**
 * Modal helpers for the Process Steps Section Card with Modal variant.
 */
import {
	decodeHtmlEntities,
	escapeHtml,
	serializeForHtmlJson,
} from '../shared/html-entities';
import {
	DEFAULT_CTA_LABEL,
	LAYOUT_VARIANTS,
	STEPPER_ARROW_URL,
	resolveDisplayIconUrl,
} from './constants';
import { createDefaultModalContent } from './modal-constants';

export { decodeHtmlEntities, escapeHtml, serializeForHtmlJson };

/**
 * Plain-text badge label, with HTML entities decoded.
 *
 * @param {Object|string} badge Badge object or label string.
 * @return {string} Label.
 */
export function getBadgeLabel( badge ) {
	const label = typeof badge === 'string' ? badge : badge?.label || '';
	return decodeHtmlEntities( label );
}

const RICH_TEXT_ALLOWED_TAGS = new Set( [
	'P',
	'STRONG',
	'EM',
	'B',
	'I',
	'BR',
	'SPAN',
] );

/**
 * Sanitize trusted editor HTML for modal output.
 *
 * @param {string} value Raw HTML or plain text.
 * @return {string} Safe HTML string.
 */
export function sanitizeRichHtml( value ) {
	const stringValue = String( value || '' ).trim();

	if ( ! stringValue ) {
		return '';
	}

	const decodedValue = decodeHtmlEntities( stringValue );

	if ( ! /[<>]/.test( decodedValue ) ) {
		return `<p>${ escapeHtml( decodedValue ) }</p>`;
	}

	if ( typeof DOMParser === 'undefined' ) {
		return `<p>${ escapeHtml( decodedValue ) }</p>`;
	}

	const doc = new DOMParser().parseFromString( decodedValue, 'text/html' );

	const sanitizeNode = ( node ) => {
		Array.from( node.children ).forEach( ( child ) => {
			if ( ! RICH_TEXT_ALLOWED_TAGS.has( child.tagName ) ) {
				while ( child.firstChild ) {
					child.parentNode.insertBefore( child.firstChild, child );
				}
				child.remove();
				sanitizeNode( node );
				return;
			}

			Array.from( child.attributes ).forEach( ( attribute ) => {
				child.removeAttribute( attribute.name );
			} );

			sanitizeNode( child );
		} );
	};

	sanitizeNode( doc.body );

	return doc.body.innerHTML;
}

/**
 * Normalize stored modal content with safe defaults.
 *
 * @param {Object} modalContent Stored modal content.
 * @param {string} title        Card title fallback.
 * @return {Object} Normalized modal content.
 */
export function normalizeModalContent( modalContent = {}, title = '' ) {
	const defaults = createDefaultModalContent( title );
	const stored =
		modalContent && typeof modalContent === 'object' ? modalContent : {};

	const processSteps = Array.isArray( stored.processSteps )
		? stored.processSteps.map( ( step ) => ( {
				title: decodeHtmlEntities( step?.title || '' ),
				description: step?.description || '',
		  } ) )
		: defaults.processSteps;

	const badges = Array.isArray( stored.badges )
		? stored.badges
				.filter( ( badge ) => badge && ( badge.label || badge.color ) )
				.map( ( badge ) => ( {
					label: decodeHtmlEntities( badge.label || '' ),
					color: badge.color || '',
				} ) )
		: defaults.badges;

	return {
		whatTitle: decodeHtmlEntities(
			stored.whatTitle ?? defaults.whatTitle
		),
		whatText: stored.whatText ?? defaults.whatText,
		approachTitle: decodeHtmlEntities(
			stored.approachTitle ?? defaults.approachTitle
		),
		approachText: stored.approachText ?? defaults.approachText,
		processSteps,
		whyTitle: decodeHtmlEntities( stored.whyTitle ?? defaults.whyTitle ),
		whyText: stored.whyText ?? defaults.whyText,
		commonUsesTitle: decodeHtmlEntities(
			stored.commonUsesTitle ?? defaults.commonUsesTitle
		),
		badges,
	};
}

/**
 * Build unique modal element ids for a block instance.
 *
 * @param {string} blockInstanceId Block instance id.
 * @param {string} suffix          Element suffix.
 * @return {string} Unique id.
 */
export function getModalElementId( blockInstanceId, suffix ) {
	const safeId = String( blockInstanceId || 'block' ).replace(
		/[^a-zA-Z0-9_-]/g,
		''
	);
	return `alteia-pss-modal-${ safeId }-${ suffix }`;
}

/**
 * Build modal root id.
 *
 * @param {string} blockInstanceId Block instance id.
 * @return {string} Modal root id.
 */
export function getModalRootId( blockInstanceId ) {
	return getModalElementId( blockInstanceId, 'root' );
}

/**
 * Markup for the down-arrow connector between modal stepper items.
 *
 * @return {string} Arrow HTML.
 */
export function renderStepperArrowHtml() {
	return `<div class="alteia-pss-modal__stepper-arrow" aria-hidden="true">
		<img src="${ escapeHtml(
			STEPPER_ARROW_URL
		) }" alt="" width="15" height="26" decoding="async" />
	</div>`;
}

/**
 * Prepare serializable modal payload for the frontend script.
 *
 * @param {Object} attributes Block attributes.
 * @return {Object} Modal data payload.
 */
export function buildModalSectionData( attributes ) {
	const { blockInstanceId, layoutVariant, steps = [] } = attributes;

	if ( layoutVariant !== LAYOUT_VARIANTS.WITH_MODAL ) {
		return {
			blockInstanceId: blockInstanceId || '',
			steps: [],
		};
	}

	return {
		blockInstanceId: blockInstanceId || '',
		steps: steps.map( ( step, index ) => ( {
			id: step.id || '',
			title: step.title || '',
			iconUrl: resolveDisplayIconUrl( step.iconUrl, index ),
			ctaLabel: step.ctaLabel || DEFAULT_CTA_LABEL,
			modalTitle: step.modalTitle || step.title || '',
			modalContent: normalizeModalContent(
				step.modalContent,
				step.title || ''
			),
		} ) ),
	};
}

/**
 * Find step modal data by id.
 *
 * @param {Object} sectionData Section modal data.
 * @param {string} stepId      Step id.
 * @return {Object|null} Step modal data.
 */
export function findStepModalData( sectionData, stepId ) {
	if ( ! sectionData?.steps || ! stepId ) {
		return null;
	}

	return sectionData.steps.find( ( step ) => step.id === stepId ) || null;
}
